import { useMutation } from "@tanstack/react-query"
// context
import useAuthenticationContext from "context/Authentication/useAuthenticationContext"
// api
import { getExternalCertificate, GetExternalCertificateResponse } from "api"
// utils
import { downloadBase64File } from "utils/downloadBase64File"

// Built on useMutation rather than useQuery: this always needs to run as an explicit,
// on-demand action (triggered by a click, or by the payment-return retry loop) rather
// than a cached-by-key query. A useQuery with enabled: false, triggered only through a
// manual refetch(), does not reliably fire its onSuccess callback - which silently
// dropped the download even though the fetch itself succeeded. Mutations don't have
// that gap: onSuccess always fires for every call, and superCourseId is passed fresh
// per call instead of being baked into a query key/closure.
const useDownloadExternalCertificate = (): {
  fetchExternalCertificate: (superCourseId: number) => Promise<GetExternalCertificateResponse | undefined>
  externalCertificateLoading: boolean
} => {
  const {
    user: { id: userId, name: userFullName },
  } = useAuthenticationContext()

  const downloadCertificate = (base64Pdf: string) => {
    downloadBase64File(base64Pdf, `Medudy Lung Cancer Screening Certificate, ${userFullName}.pdf`)
  }

  const { mutateAsync, isLoading: externalCertificateLoading } = useMutation({
    mutationFn: (superCourseId: number) => getExternalCertificate({ userId, superCourseId }),
    onSuccess: (data) => {
      if (data?.base64Pdf) downloadCertificate(data.base64Pdf)
    },
  })

  const fetchExternalCertificate = async (superCourseId: number) => {
    try {
      return await mutateAsync(superCourseId)
    } catch {
      return undefined
    }
  }

  return {
    fetchExternalCertificate,
    externalCertificateLoading,
  }
}

export default useDownloadExternalCertificate
